import jsPDF from 'jspdf';
import type { TimeTrackerInvoiceConfig, TimeTrackerClient } from '@/types/pm-types';

export interface InvoiceLineItem {
  description: string;
  hours: number;
  rate: number;
  amount: number;
}

export interface InvoicePDFOptions {
  invoiceConfig: TimeTrackerInvoiceConfig;
  lineItems: InvoiceLineItem[];
  client: TimeTrackerClient;
  subtotal: number;
  tax: number;
  total: number;
}

export function generateInvoicePDF(config: InvoicePDFOptions): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // ─── Header ──────────────────────────────────────────────
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', 14, y);

  if (config.invoiceConfig.invoiceNumber) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`#${config.invoiceConfig.invoiceNumber}`, pageWidth - 14, y, { align: 'right' });
  }

  y += 12;

  // ─── Company Info (left) ─────────────────────────────────
  if (config.invoiceConfig.companyName) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(config.invoiceConfig.companyName, 14, y);
    y += 6;
  }
  if (config.invoiceConfig.companyAddress) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const lines = config.invoiceConfig.companyAddress.split('\n');
    for (const line of lines) {
      doc.text(line, 14, y);
      y += 4.5;
    }
  }

  y += 4;

  // ─── Invoice Meta ────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  const metaX = pageWidth - 14;
  let metaY = 38;
  doc.text(`Date: ${new Date().toLocaleDateString()}`, metaX, metaY, { align: 'right' });
  metaY += 5;
  doc.text(`Period: ${config.invoiceConfig.dateRange.start} to ${config.invoiceConfig.dateRange.end}`, metaX, metaY, { align: 'right' });
  if (config.invoiceConfig.purchaseOrder) {
    metaY += 5;
    doc.text(`PO: ${config.invoiceConfig.purchaseOrder}`, metaX, metaY, { align: 'right' });
  }

  y = Math.max(y, metaY + 8);

  // ─── Bill To ─────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Bill To:', 14, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(config.client.name, 14, y);
  y += 5;
  if (config.client.contactEmail) {
    doc.setFontSize(9);
    doc.text(config.client.contactEmail, 14, y);
    y += 5;
  }
  if (config.client.address) {
    doc.setFontSize(9);
    const addrLines = config.client.address.split('\n');
    for (const line of addrLines) {
      doc.text(line, 14, y);
      y += 4.5;
    }
  }

  y += 10;

  // ─── Line Items Table ───────────────────────────────────
  const colX = { desc: 14, hours: 110, rate: 140, amount: 170 };

  // Table header
  doc.setFillColor(245, 245, 245);
  doc.rect(14, y - 4, pageWidth - 28, 8, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Description', colX.desc, y);
  doc.text('Hours', colX.hours, y, { align: 'right' });
  doc.text('Rate', colX.rate, y, { align: 'right' });
  doc.text('Amount', colX.amount, y, { align: 'right' });
  y += 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  // Table rows
  for (const item of config.lineItems) {
    if (y > 260) {
      doc.addPage();
      y = 20;
    }
    doc.text(item.description.slice(0, 50), colX.desc, y);
    doc.text(item.hours.toFixed(1), colX.hours, y, { align: 'right' });
    doc.text(`$${item.rate.toFixed(2)}`, colX.rate, y, { align: 'right' });
    doc.text(`$${item.amount.toFixed(2)}`, colX.amount, y, { align: 'right' });
    y += 6;
  }

  // ─── Separator line ──────────────────────────────────────
  y += 4;
  doc.line(100, y, pageWidth - 14, y);
  y += 8;

  // ─── Totals ──────────────────────────────────────────────
  doc.setFontSize(10);
  doc.text('Subtotal:', 130, y);
  doc.text(`$${config.subtotal.toFixed(2)}`, colX.amount, y, { align: 'right' });
  y += 6;

  if (config.tax > 0) {
    const taxRate = config.invoiceConfig.taxRate ?? 0;
    doc.text(`Tax (${taxRate}%):`, 130, y);
    doc.text(`$${config.tax.toFixed(2)}`, colX.amount, y, { align: 'right' });
    y += 6;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Total:', 130, y);
  doc.text(`$${config.total.toFixed(2)}`, colX.amount, y, { align: 'right' });

  // ─── Notes ───────────────────────────────────────────────
  if (config.invoiceConfig.notes) {
    y += 16;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', 14, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    const noteLines = doc.splitTextToSize(config.invoiceConfig.notes, pageWidth - 28);
    doc.text(noteLines, 14, y);
  }

  // ─── Save ────────────────────────────────────────────────
  doc.save(`Invoice_${config.invoiceConfig.invoiceNumber || 'draft'}.pdf`);
}
